import { useQuery } from '@tanstack/react-query';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import * as pointsLeaderboardApi from '@/api/pointsLeaderboard';
import type { PointsLeaderboardResponse, RankingType } from '@/types/schemas';
import type { TimeRange } from '@/api/pointsLeaderboard';

interface UsePointsLeaderboardOptions {
  range?: TimeRange;
  page?: number;
  limit?: number;
  type?: RankingType;
  enabled?: boolean;
}

/**
 * Hook to fetch the general points leaderboard
 * Supports three types: burned (spent), cumulative (total earned), unused (current balance)
 * This is separate from the lottery leaderboard which tracks current season contributions
 */
export function usePointsLeaderboard({
  range = 'all',
  page = 1,
  limit = 10,
  type = 'burned',
  enabled = true,
}: UsePointsLeaderboardOptions = {}) {
  return useQuery<PointsLeaderboardResponse>({
    queryKey: ['pointsLeaderboard', range, page, limit, type],
    queryFn: async () => {
      try {
        const result = await pointsLeaderboardApi.getPointsLeaderboard({
          range,
          page,
          limit,
          type,
        });
        return result;
      } catch (error) {
        console.error('[usePointsLeaderboard] Error:', error);
        throw error;
      }
    },
    enabled: enabled, // Removed isConnected requirement - endpoint is @Public()
    staleTime: 5 * 60_000, // 5 minutes - reduce API calls
    gcTime: 10 * 60_000, // 10 minutes
    refetchInterval: false, // Disable auto-refresh to reduce API load (fetching 200 users)
    refetchOnWindowFocus: false, // Disable refetch on focus
    refetchOnMount: true, // Override global default - fetch on mount
    retry: 3, // Retry on failure
  });
}

/**
 * Hook to fetch the current user's rank in the points leaderboard
 */
export function useMyPointsRank(range: TimeRange = 'all', type: RankingType = 'burned', enabled = true) {
  const { isConnected } = useWalletAddress();

  return useQuery({
    queryKey: ['pointsLeaderboard', 'myRank', range, type],
    queryFn: async () => {
      try {
        const result = await pointsLeaderboardApi.getMyRank({ range, type });
        return result;
      } catch (error) {
        console.error('[useMyPointsRank] Error:', error);
        throw error;
      }
    },
    enabled: isConnected && enabled, // Requires authentication for user-specific rank + feature flag check
    staleTime: 5 * 60_000, // 5 minutes
    gcTime: 10 * 60_000, // 10 minutes
    refetchInterval: false, // Disable auto-refresh to reduce API load
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Override global default
    retry: 3,
  });
}
